import React from 'react';
import {
  TextField,
  FormControl,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { CalendarToday, Clear } from '@mui/icons-material';
import PropTypes from 'prop-types';

const DatePicker = ({
  value,
  onChange,
  label = 'Select Date',
  name,
  required = false,
  disabled = false,
  error = false,
  helperText = '',
  minDate,
  maxDate,
  fullWidth = true,
  size = 'medium',
  variant = 'outlined',
  placeholder,
  allowPastDates = false,
  allowTodayAndFuture = true,
  showClearButton = false,
  clearButtonLabel = 'Clear date',
  //dateFormat = 'YYYY-MM-DD',
  startAdornment,
  endAdornment,
  showCalendarIcon = true,
  ...otherProps
}) => {
  // Calculate min and max dates
  const getMinDate = () => {
    if (minDate) {
      return typeof minDate === 'string'
        ? minDate
        : minDate.toISOString().split('T')[0];
    }

    if (!allowPastDates && allowTodayAndFuture) {
      return new Date().toISOString().split('T')[0]; // Today onwards
    }

    if (!allowPastDates && !allowTodayAndFuture) {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      return tomorrow.toISOString().split('T')[0]; // Tomorrow onwards
    }

    return undefined; // No minimum restriction
  };

  const getMaxDate = () => {
    if (maxDate) {
      return typeof maxDate === 'string'
        ? maxDate
        : maxDate.toISOString().split('T')[0];
    }
    return undefined;
  };

  const handleChange = (event) => {
    const dateValue = event.target.value;
    if (onChange) {
      onChange(event, dateValue);
    }
  };

  const handleClear = () => {
    if (onChange && !disabled) {
      const syntheticEvent = {
        target: { name, value: '' },
      };
      onChange(syntheticEvent, '');
    }
  };

  // Generate default start adornment
  const getDefaultStartAdornment = () => {
    if (!showCalendarIcon) return null;

    return (
      <InputAdornment position='start'>
        <CalendarToday
          sx={{
            color: disabled ? 'rgba(0, 0, 0, 0.38)' : 'action.active',
            fontSize: size === 'small' ? '1rem' : '1.25rem',
          }}
        />
      </InputAdornment>
    );
  };

  // Generate default end adornment
  const getDefaultEndAdornment = () => {
    if (!showClearButton || !value || disabled) return null;

    return (
      <InputAdornment position='end'>
        <IconButton
          onClick={handleClear}
          edge='end'
          size={size}
          title={clearButtonLabel}
          sx={{
            visibility: value ? 'visible' : 'hidden',
          }}
        >
          <Clear fontSize={size === 'small' ? 'small' : 'medium'} />
        </IconButton>
      </InputAdornment>
    );
  };

  // Determine final adornments
  const finalStartAdornment =
    startAdornment !== undefined ? startAdornment : getDefaultStartAdornment();
  const finalEndAdornment =
    endAdornment !== undefined ? endAdornment : getDefaultEndAdornment();

  // Format helper text with date format info
  const getFormattedHelperText = () => {
    if (error || helperText) {
      return helperText;
    }

    //const formatInfo = `Format: ${dateFormat}`;
    const minDateInfo = getMinDate() ? `Min: ${getMinDate()}` : '';
    const maxDateInfo = getMaxDate() ? `Max: ${getMaxDate()}` : '';

    return `${minDateInfo}${maxDateInfo}`;
  };

  return (
    <FormControl fullWidth={fullWidth} error={error}>
      <TextField
        name={name}
        value={value || ''}
        onChange={handleChange}
        label={label}
        type='date'
        variant={variant}
        size={size}
        required={required}
        disabled={disabled}
        error={error}
        helperText={getFormattedHelperText()}
        fullWidth={fullWidth}
        placeholder={placeholder}
        InputLabelProps={{
          shrink: true,
          required: required,
        }}
        inputProps={{
          min: getMinDate(),
          max: getMaxDate(),
          ...otherProps.inputProps,
        }}
        InputProps={{
          startAdornment: finalStartAdornment,
          endAdornment: finalEndAdornment,
          ...otherProps.InputProps,
        }}
        sx={{
          '& input[type="date"]::-webkit-calendar-picker-indicator': {
            cursor: 'pointer',
            opacity: showCalendarIcon ? 0 : 1, // Hide native picker when custom icon is shown
            filter: disabled ? 'grayscale(1)' : 'none',
            position: 'absolute',
            right: showClearButton && value ? '40px' : '8px',
            width: '20px',
            height: '20px',
            zIndex: 2, // Ensure it's above other elements
          },
          '& input[type="date"]': {
            cursor: disabled ? 'not-allowed' : 'pointer',
            paddingLeft: showCalendarIcon
              ? size === 'small'
                ? '32px'
                : '36px'
              : '8px',
            paddingRight: showClearButton && value ? '20px' : '40px', //in Small screens the date  part is covered by padding Right
          },
          '& input[type="date"]:disabled': {
            cursor: 'not-allowed',
            color: 'rgba(0, 0, 0, 0.38)',
          },
          '& .MuiInputAdornment-positionStart': {
            position: 'absolute',
            left: '8px',
            zIndex: 1,
            pointerEvents: 'none',
          },
          ...otherProps.sx,
        }}
        {...otherProps}
      />
    </FormControl>
  );
};

DatePicker.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  label: PropTypes.string,
  name: PropTypes.string,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  error: PropTypes.bool,
  helperText: PropTypes.string,
  minDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  maxDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  fullWidth: PropTypes.bool,
  size: PropTypes.oneOf(['small', 'medium']),
  variant: PropTypes.oneOf(['outlined', 'filled', 'standard']),
  placeholder: PropTypes.string,
  allowPastDates: PropTypes.bool,
  allowTodayAndFuture: PropTypes.bool,
  showClearButton: PropTypes.bool,
  clearButtonLabel: PropTypes.string,
  //dateFormat: PropTypes.string,
  startAdornment: PropTypes.node,
  endAdornment: PropTypes.node,
  showCalendarIcon: PropTypes.bool,
};

export default DatePicker;
